import { INTERVIEW_JOBS, INTERVIEW_KEYWORDS, JOB_DETAIL_PATHS } from '../schemas/interviewSchema';

export const INTERVIEW_JOB_FILTERS = INTERVIEW_JOBS;
export { INTERVIEW_KEYWORDS, JOB_DETAIL_PATHS };

const img = (slug, file) => `/images/interview/${slug}/${file}`;

export const getJobMeta = (interview) =>
  INTERVIEW_JOBS.find((job) => job.id === interview.job) || INTERVIEW_JOBS[0];

export const getJobTagEn = (interview) =>
  interview.jobTagEn || getJobMeta(interview).tag;

export const getJobLine = (interview) => {
  const label = interview.jobLabel || getJobMeta(interview).label;
  if (interview.joinYear) return `${label}／${interview.joinYear}年入社`;
  return label;
};

export const getRomanName = (interview) =>
  (interview.nameEn || '').replace(/\s+/g, ' ').trim().toUpperCase();

const padNum = (index) => String(index + 1).padStart(2, '0');

const compactText = (text) => String(text || '').replace(/\s/g, '');

const leadDuplicatesBody = (lead, body) => {
  const compactLead = compactText(lead).replace(/…$/, '');
  const compactBody = compactText(body);
  return Boolean(compactLead && compactBody.startsWith(compactLead));
};

const toQaSection = (id, heading, section = {}) => {
  const body = section.body || '';
  if (!body) return null;
  const lead = leadDuplicatesBody(section.lead, body) ? '' : section.lead || '';
  return {
    id,
    heading,
    lead,
    body,
    image: section.image,
    pullQuote: section.pullQuote,
    extraLead: section.extraLead || '',
    extraBody: section.extraBody || ''
  };
};

export const getQaItems = (interview) => {
  if (Array.isArray(interview.qa) && interview.qa.length > 0) {
    return interview.qa.map((item, index) => ({
      ...item,
      num: item.num || padNum(index),
      heading: item.heading === '入社後のギャップ' ? '入社後の変化' : item.heading,
      lead: leadDuplicatesBody(item.lead, item.body) ? '' : item.lead || '',
      extraLead: item.extraLead || '',
      extraBody: item.extraBody || ''
    }));
  }

  const items = [
    toQaSection('work', '現在の仕事', interview.currentWork),
    toQaSection('career', '入社前のキャリア', interview.careerBefore),
    toQaSection('join', '入社のきっかけ', interview.joiningReason),
    toQaSection('gap', '入社後の変化', interview.afterJoining),
    toQaSection('appeal', 'FLARETECHで働く魅力', interview.flaretechStyle),
    toQaSection('message', '応募を考えている方へ', interview.message)
  ].filter(Boolean);

  return items.map((item, index) => ({ ...item, num: padNum(index) }));
};

const photoSrc = (item) => (typeof item === 'string' ? item : item?.src || '');

export const getInterviewGalleryPhotos = (interview) => {
  const used = new Set(
    [interview.heroImage, interview.thumbnail, interview.profileImage]
      .concat(getQaItems(interview).flatMap((item) => [item.image, ...(item.images || [])].map(photoSrc)))
      .filter(Boolean)
  );

  return (interview.photos || [])
    .map((item) => ({ src: photoSrc(item), alt: item?.alt || '' }))
    .filter((item) => item.src && !used.has(item.src));
};

export const INTERVIEWS = [
  {
    slug: 'hyoga-kishikawa',
    nameJa: '岸川 彪我',
    nameEn: 'Kishikawa Hyoga',
    department: 'システム部',
    job: 'engineer',
    jobLabel: '開発エンジニア',
    jobTagEn: 'Engineer',
    keywords: ['remote'],
    previousJob: 'フロントエンド／バックエンドエンジニア',
    quote: '「今の自分に足りない知識は何か」を常に考え、将来のキャリアアップのために必要な経験を主体的に積んでいこう',
    catchCopy: '「フロントエンドへの再挑戦。理想のキャリアを求めて選んだ道」',
    techStack: ['フロントエンド', '詳細設計'],
    relatedJobPath: '/jobs/development-engineer',
    thumbnail: img('hyoga-kishikawa', 'thumb.jpg'),
    heroImage: `${img('hyoga-kishikawa', 'hero.jpg')}?v=2`,
    profileImage: img('hyoga-kishikawa', 'profile.jpg'),
    hasArticle: true,
    featuredOnHome: true,
    status: 'published',
    order: 1,
    currentWork: {
      heading: '現在の業務',
      lead: 'レガシーシステムのリニューアル。詳細設計からフロントエンド開発を牽引',
      body: '現在は、古いシステムのリニューアルプロジェクトに参画しています。詳細設計以降の工程を担当しており、主にフロントエンドの開発領域をメインで担当しています。'
    },
    careerBefore: {
      heading: '転職のきっかけ',
      body: 'もともと前々職でフロントエンドエンジニアとしてキャリアをスタートしました。その後、「バックエンドの知識も身につけたい」と考え、一度転職をしてバックエンド開発を経験しました。\n\n一通りの経験を積む中で、やはり自分はフロントエンドの領域が強みであり、再度その案件に深く携わっていきたいという想いが強くなったことが、転職のきっかけとなりました。'
    },
    joiningReason: {
      heading: 'FLARETECHを知ったきっかけと、入社の決め手',
      lead: 'スピーディーな採用プロセスと、理想の働きやすさが決め手',
      body: '転職サイトに自分の経歴を登録していた際、FLARETECHからスカウトをいただいたのが知ったきっかけです。\n\n数ある企業の中から入社を選んだ決め手は、選考から採用にいたるまでの流れが非常にスピーディーだったこと。そして、リモートワークの実現が可能だというお話を面接時に伺い、自身のパフォーマンスを発揮しやすい環境だと考えたためです。'
    },
    afterJoining: {
      heading: '入社後の変化',
      lead: '貪欲なインプットと実践のサイクル。キャリアを見据えた高い意識',
      body: '前職や前々職の時と比べて、自発的に知識を取り入れる機会が格段に増えました。技術書を読んだり、最新の技術記事をチェックしたりする習慣がつき、さらにそれを実際の現場で試せる環境があるため、どんどん知識が自分のものになっていく感覚があります。\n\n「今の自分に足りない知識は何か」を常に考え、将来のキャリアアップのために必要な経験を主体的に積んでいこうという意識が、入社してからより強くなりました。'
    },
    flaretechStyle: {
      heading: 'FLARETECHらしさ',
      lead: 'エンジニアの「成長と働きやすさ」にフォーカスした、変化を続ける仕組み',
      body: '私自身、そこまで社内の取り組みに参加しているわけではないのですが（笑）、そんな客観的な視点から見ても、今のFLARETECHは面白い変化を遂げていると感じます。\n\n最近では自社開発アプリの「lensAI」を導入して社内の1on1に活用するなど、エンジニアのキャリアアップや働きやすさに真剣にフォーカスを当てた取り組みが次々と行われています。今まさに挑戦したいと考えているのは、社内で「スクラム開発のワークショップイベント」を企画・開催すること。こうした個人の発案や挑戦を後押ししてくれる土壌があります。'
    },
    message: {
      heading: 'FLARETECHが合う人とは / 応募者へのメッセージ',
      lead: '求めるのは「自立したエンジニア」。共に会社の勢いに乗っていこう',
      body: 'どのような働き方を選ぶにしても、自分を律して動ける「自立できている人」であれば、どこまでも続けていけるし輝ける環境だと感じています。\n\n一言で言うと、FLARETECHは「今まさに勢いに乗っている会社」です。社内ではAIハッカソンや最先端の勉強会が定期的に開催されており、AIを用いたモダンな開発などにもチャレンジできる環境が揃っています。ぜひ、私たちと一緒に開発の波に乗っていきましょう！',
      image: img('hyoga-kishikawa', 'message.jpg')
    },
    skillGrowth: [],
    metaTitle: '岸川 彪我 | 社員インタビュー | FLARETECH 採用',
    metaDescription: 'フロントエンドへの再挑戦をきっかけにFLARETECHへ入社した岸川 彪我さんのインタビュー。レガシーシステム刷新に携わるエンジニアのリアルな声。'
  },
  {
    slug: 'miki-terada',
    nameJa: '寺田 美樹',
    nameEn: 'Terada Miki',
    department: 'システム部',
    job: 'engineer',
    jobLabel: '開発エンジニア / 部長',
    jobTagEn: 'Engineer',
    keywords: ['management'],
    previousJob: 'IT企業（開発）',
    quote: '河畑さんは少しも濁さず、はっきりと「見捨てません」と言い切ってくださいました',
    catchCopy: '「見捨てない」──初対面の私に語ってくれた、社長の誠実さと強い熱意',
    techStack: ['React', 'フロントエンド', 'インフラ', 'スクラム'],
    relatedJobPath: '/jobs/development-engineer',
    thumbnail: img('miki-terada', 'thumb.jpg'),
    heroImage: `${img('miki-terada', 'hero.jpg')}?v=2`,
    profileImage: img('miki-terada', 'profile.jpg'),
    heroObjectPosition: '70% 18%',
    hasArticle: true,
    featuredOnHome: true,
    status: 'published',
    order: 2,
    currentWork: {
      heading: '現在の業務',
      lead: '車両データ分析システムのフロントエンド開発。インフラから仕様調整まで幅広く参画',
      body: '車両データ分析システムのUI部分の開発を担当しております。アプリ開発からインフラ構築まで幅広く携わっており、開発はスクラム形式で進めています。関係者と仕様を調整する機会も多く、チームメンバーと協力しながら日々の業務にあたっております。'
    },
    careerBefore: {
      heading: 'FLARETECH入社前のキャリアと、転職のきっかけ',
      lead: '技術が伸びないもどかしさと、キャリアを阻む壁。「バリバリ働ける環境」を求めて',
      body: '前職もIT企業に席を置いていましたが、当時は非常に労働時間が長く、自分の努力だけでは変えられない古い体質が残る環境でした。\n\nなにより悔しかったのは、どれだけ成果を出して実績を積んでも、性別や固定観念によって評価やキャリアアップの道が制限されてしまう現実を突きつけられたことです。大手企業ではありましたが、「正当に評価され、自分の力でバリバリ働ける環境を見つけたい」と強く思い、転職活動を始めました。'
    },
    joiningReason: {
      heading: 'FLARETECHを知ったきっかけと、入社の決め手',
      body: '当時は現職が多忙を極め、自力で企業を探す余裕がなかったため、スカウトサイトに経歴書を登録していました。そこでFLARETECH（当時Marvel）からオファーをいただいたのが知ったきっかけです。実は、並行して他の大手企業からもオファーをいただいていました。当時、FLARETECHはまだ社員数20名ほどの規模だったためギリギリまで悩んだのですが、最終的な決め手となったのは代表の河畑さんの「圧倒的な熱意」でした。\n\n「エンジニアの待遇を絶対に良くしていきたい」「より良い社会を創りたい」という想いが、言葉の端々から真っ直ぐに伝わってきたんです。また、面接の中で、私が「もしエンジニアが病気やうつ病などになってしまったらどうしますか？」と少し踏み込んだ質問をした際、河畑さんは少しも濁さず、はっきりと「見捨てません」と言い切ってくださいました。さらに、当時課題を抱えていたメンバーに対しても「会社全体でフォローしていく」という内部のリアルな状況まで、初対面の私に誠実に話してくれたのです。この嘘のない姿勢に深く心を打たれ、「この会社でチャレンジしよう」と入社を決めました。'
    },
    afterJoining: {
      heading: '入社後の変化',
      lead: '基礎的な作業から「勉強会の講師」へ。チャンスをもらえる環境で磨かれた設計力',
      body: 'まず技術面でいうと、社内勉強会などでReact等の講師を任せていただくくらいには成長できたなとすごく感じています。\n\n前職は人海戦術のような感じでテストフェーズの対応などが結構多く、エンジニアとして技術力を伸ばせたと感じることがあまりありませんでした。本当に、誰もが知っているような基礎の基礎ばかりをやっていたんです。\n\nFLARETECHに入社してからは、色々な上流工程なども経験させていただきました。そのため、エンジニアとしての純粋な技術力もそうですし、アプリを作るうえでの設計力もきちんと養われたなと感じています。'
    },
    flaretechStyle: {
      heading: 'FLARETECHらしさ',
      lead: '離れていても感じる人の温かさと、挑戦への提案に「いいよ」と二つ返事で応えてくれるスピード感',
      body: 'FLARETECHのエンジニアは全国津々浦々、色々な場所でプロジェクトに参画していますよね。実際に対面でお会いする機会はかなり少ないと思うのですが、それでもしっかりと社内のつながりを感じられる社風になっているなと思います。\n\n例えば、Slackの雑談チャンネルでは皆さんが自由に発信されていたり、テックチャンネルなどで困っていることを相談すると、すぐに周りのメンバーが助け船を出してくれたり。そういった「人の温かさ」をしっかりと感じられる環境です。\n\nあとはボトムアップな空気感も本物で、「こういうことをやってみたい」という話を役員の方々にさせていただくと、すぐに「いいよ」と二つ返事でやらせていただくことも多々あって、最初は本当にびっくりしました。'
    },
    message: {
      heading: 'FLARETECHが合う人とは / 応募者へのメッセージ',
      lead: '挑戦者、求む！周りと協力しながら、どこまでも成長したい方へ',
      body: '入社前の自分に声をかけるとしたら、真っ先に「その選択は間違ってなかったよ！」と言いたいです。やっぱり入社前はいろいろと不安もあったので、当時の自分を安心させてあげたいですね。\n\n一言でFLARETECHを表現するなら、やはり「熱意」ですね。それに伴う勢いもすごくある会社だと思います。\n\n社内の横のつながりがとても強い企業なので、周りと協力しながら色んなことにチャレンジしようと思える方には、間違いなくすごく合っている環境です。まさに「挑戦者、求む！」というキャッチフレーズがぴったりだと思います。どんどんどんどん成長していきたい方、ぜひお待ちしております！',
      image: img('miki-terada', 'message.jpg')
    },
    skillGrowth: [],
    metaTitle: '寺田 美樹 | 社員インタビュー | FLARETECH 採用',
    metaDescription: '代表の誠実さに惹かれて入社した寺田 美樹さん（システム部 部長）のインタビュー。正当に評価され、挑戦できる環境を求めたキャリアストーリー。'
  },
  {
    slug: 'naoki-tsukada',
    nameJa: '塚田 直希',
    nameEn: 'Tsukada Naoki',
    department: 'システム部',
    job: 'engineer',
    jobLabel: '開発エンジニア',
    jobTagEn: 'Engineer',
    keywords: ['inexperienced'],
    previousJob: '新卒1期生（未経験入社）',
    quote: 'まだ誰も歩んでいない道を切り拓き、会社と一緒に自分自身も成長できるチャンスだと思い、入社を決めました',
    catchCopy: '会社とともに成長できる「新卒1期生」に惹かれて入社',
    techStack: ['AI', 'チームリード', '要件定義'],
    relatedJobPath: '/jobs/development-engineer',
    thumbnail: img('naoki-tsukada', 'thumb.jpg'),
    heroImage: `${img('naoki-tsukada', 'hero.jpg')}?v=2`,
    profileImage: img('naoki-tsukada', 'profile.jpg'),
    heroObjectPosition: '70% 28%',
    hasArticle: true,
    featuredOnHome: true,
    status: 'published',
    order: 3,
    currentWork: {
      heading: '現在の業務',
      lead: 'AIを活用した医療アプリ開発と、チームマネジメント',
      body: '歯科医療向けアプリの受託開発プロジェクトにて、AIを活用した医院用・患者用双方のシステム構築を担っています。現在は開発業務に留まらず、プロジェクトのチームリードを任せていただいています。フロントに立ってお客さまと要件を詰めたり、メンバーへのタスクの役割分担を適切に行ったりと、ビジネス視点を持ってプロジェクトを推進しています。\n\nまた、社内ではFLARETECHの新卒1期生として入社した経験を活かし、今年度（2026年度）に新卒として入社したエンジニア5名のメンターも担当しています。一人一人の違った考え方を聞くと非常に学びになるので、楽しみながらメンターをやっています。'
    },
    careerBefore: {
      heading: '',
      body: ''
    },
    joiningReason: {
      heading: 'FLARETECHを知ったきっかけと、入社の決め手',
      body: '就職活動を始める際に、エンジニア職について調べていく中で、エンジニアは個人作業が多いイメージでしたが、思っていた以上に「ビジネス的な視点」も求められる仕事だと知り、非常に興味が湧きました。\n\nエンジニア職に絞って就職活動をしていた中で、エージェントからの紹介でオンライン説明会に参加したのがFLARETECHを知ったきっかけです。当時は就活を始めたばかりで、こういう勢いのある企業があるんだと新鮮な学びになったのを覚えています。\n\n最終的な決め手は、自分たちの代が「新卒1期生」になるという点です。まだ誰も歩んでいない道を切り拓き、会社と一緒に自分自身も成長できるチャンスだと思い、入社を決めました。'
    },
    afterJoining: {
      heading: '入社後の変化',
      lead: '先輩社員との会話で気づいた、目の前のタスクに迷わない「ゴールの立て方」',
      body: '未経験での入社だったため、スキル面での成長はもちろん実感できている部分がありますが、一番は「道筋を立て、目的を決めて頑張る意識」が身についたことです。\n\n入社当初は「なんとなく頑張る」という状態になりがちでしたが、先輩社員から「中間地点のゴールを細かくたくさん設定していくと、進むべき道筋が見えてくるよ」とアドバイスをいただきました。その言葉が自分の中にスッと入ってきたんです。\n\nそれからは、AIと壁打ちしながら期限を決めて、「ここまでにこの状態を目指す」という小さなゴールを設定して進めるようになりました。今、自分が何をすべきかが明確になったことで、日々の成長を実感できる機会が格段に増えました。'
    },
    flaretechStyle: {
      heading: 'FLARETECHらしさ',
      lead: '若い活気×ベテランの気さくさ。技術ディスカッションが自然に生まれる「社内ハッカソン」の面白さ',
      body: 'まず、若いメンバーが多くて活気があるところですね。一方でベテラン層の方々もすごく気さくで、私たちの目線に立ってフラットに話をしてくださるので、とにかくコミュニケーションが取りやすい環境です。オフライン・オンラインの交流会や社内勉強会など、横のつながりを作れる機会や場も豊富に用意されています。\n\n特に面白いのが「社内ハッカソン」です。ただ技術を競うだけでなく、「優勝した人の作品、どうやって実装したんだろう？」と、参加メンバーの間で自然と技術的なディスカッションが生まれます。ハッカソンをきっかけに、仲間と楽しみながらスキルアップに向かえる風土が、FLARETECHには自然と根付いています。'
    },
    message: {
      heading: 'FLARETECHが合う人とは / 応募者へのメッセージ',
      lead: '「前向きに挑戦できる人」',
      body: '一言でいえば「前向きに頑張れる人」です。勉強会やハッカソン、サポート体制など、キャッチアップできる環境は揃っているので、それを自ら活かして実行できる人に合う環境だと思います。\n\n現在のFLARETECHを一言で表すなら、まさに『成長中』です。営業や採用、自社プロダクトの開発など、常に新しいことに挑戦していて、今後も新しいことが始まっていくワクワク感を、ぜひ一緒に味わいたいですし、自分はエンジニアとしてまだ未熟な部分が多いので、おせっかいなくらい色々教えていただきたいです！',
      image: img('naoki-tsukada', 'message.jpg')
    },
    skillGrowth: [],
    metaTitle: '塚田 直希 | 社員インタビュー | FLARETECH 採用',
    metaDescription: 'FLARETECH新卒1期生の塚田 直希さんインタビュー。AIを活用した医療アプリ開発と新卒メンターとして、会社とともに成長するリアル。'
  },
  {
    slug: 'shimizu',
    nameJa: '清水 範人',
    nameEn: 'Shimizu Norihito',
    department: 'システム部',
    job: 'engineer',
    jobLabel: '開発エンジニア / 部長',
    jobTagEn: 'Engineer',
    keywords: ['management', 'career-change'],
    previousJob: '学習塾、プログラマー／SE、事業会社PM',
    quote: '「これからの先を作っていく・伸ばしていく」というフェーズの会社の方が圧倒的に面白そうだと感じた',
    catchCopy: '高条件のオファーよりも魅力的だった、「これからの組織を創る」ベンチャーの活気',
    techStack: ['プロジェクトマネジメント', '組織化'],
    relatedJobPath: '/jobs/development-engineer',
    thumbnail: img('shimizu', 'thumb.jpg'),
    heroImage: img('shimizu', 'hero.jpg'),
    profileImage: img('shimizu', 'profile.jpg'),
    heroObjectPosition: '68% 42%',
    hasArticle: true,
    featuredOnHome: true,
    status: 'published',
    order: 4,
    currentWork: {
      heading: '現在の業務',
      lead: '40名規模のプロジェクトマネジメントと、200名超を率いるシステム部全体の組織化',
      body: '現在は、約40名規模のメンバーが参画するプロジェクトのマネジメントを現場で指揮しながら、システム部 部長としてFLARETECHに所属する200名以上のエンジニア全体を統括しています。変化の激しいIT業界の中で、仲間が支え合い、知見を循環させながら共に成長できる組織を築くことが私のミッションです。'
    },
    careerBefore: {
      heading: 'FLARETECH入社前のキャリアと、転職のきっかけ',
      lead: '異業界からの再挑戦。紆余曲折を経て「自分が一番輝ける場所」へ',
      body: '学習塾での正社員勤務を経て、プログラマー・システムエンジニアへと転身しました。その後、約8年間にわたり経験を積んだのち、事業会社のPM（プロジェクトマネージャー）として約9年間プロジェクトを牽引してきました。\n\nしかし、激務や大きな責任の中で一度心が折れてしまい、IT業界を離れる決断をした時期があります。約1年間、まったく別の業界を経験したのですが、その中で強く実感したのは「やはり自分が一番輝ける場所はIT業界なんだ」ということでした。もう一度エンジニアとして輝くため、FLARETECHでIT業界への復帰を果たしました。'
    },
    joiningReason: {
      heading: 'FLARETECHを知ったきっかけと、入社の決め手',
      body: '転職サイトでさまざまな企業を検討し応募した中のひとつが、FLARETECH（当時Marvel）でした。\n\n率直に申し上げると、他社で当社より高い給与提示でオファーをくださった企業もありました。それでも最終的にFLARETECHを選んだ理由は、すでに体制が完成している歴史ある企業よりも、「これからの先を作っていく・伸ばしていく」というフェーズの会社の方が圧倒的に面白そうだと感じたからです。\n\n一言で決め手を表すなら、会社全体に活気があり、「若くて楽しそう」だと感じたこと。そして当時は柔軟な働き方ができる環境が整っていたことも大きな後押しとなりました。'
    },
    afterJoining: {
      heading: '入社後の変化',
      lead: '「キラキラしたIT」の裏にある泥臭さ。時代の変化を経て学んだ「動機付け」の重要性',
      body: 'メンバーとの接し方を自問自答し、柔軟に変化させられるようになった部分は、私自身の大きな変化であり成長ですね。\n\n私が若手だった頃は、ITエンジニアは「IT土方」などとも呼ばれていた時代で（笑）いわゆる“叩き上げ”の世界でした。お世辞にもキラキラしておらず、デスクトップPCの前で「どうすれば効率化して生き残れるか」を必死に考えて血肉にしてきましたし、PM時代も「納期は絶対」で泥臭くやり切るのが当たり前でした。\n\nでも最近は、ITといえばGoogleやOpenAIみたいなお洒落なオフィスやAIのイメージがあって、どこかキラキラして見えがちですよね。そうした理想と現実の現場とのギャップがある中で、ただ単に指示を出したり、「モチベーションは自分で作れ」と突き放したりするだけじゃダメなんだなと気づかされました。\n\n本質的な考え方は変わりませんが、「どうすればモチベーションを持たせてあげられるか」「どう動機付けするか」を意識し、試行錯誤しながら実践できるようになりました。自分の中にある軸はブレさせず、時代の変化に合わせてメンバーへのアプローチをアップデートできるようになったことが、入社後の一番の変化です。'
    },
    flaretechStyle: {
      heading: 'FLARETECHらしさ',
      lead: '年功序列のない「抜擢人事」と、誰もがフラットに意見を言える風通しの良さ',
      body: '特に面白いと感じる部分が2つあります。\n\n1つ目は、経験が浅い子でも思い切ってポストに充ててみると、一気に伸びるところです。大手のような年功序列もなく、かといって人が少なすぎるベンチャーのように横並びでもない。エンジニアの層が厚いからこそ「抜擢人事」がしやすく、そこで化ける人が一気に伸びていくのが面白いですね。\n\n2つ目は、立場に関係なく意見が非常に言いやすい空気感です。リーダー会や次部長会議などでも、発言者の立場や声の大きさに関わらずフラットに耳を傾けてもらえます。「言ってみるだけタダ」ではありませんが、誰もが気軽に発言でき、良い意見であれば即座に採用される風通しの良さがあります。最近では生成AIの活用やAIミニハッカソンなど、社内勉強会もAIが主軸になっていて本当は私も参加したいのですが、日々の業務の忙しさゆえに参加できていないのが悔しいところです！'
    },
    message: {
      heading: 'FLARETECHが合う人とは / 応募者へのメッセージ',
      lead: '求めるのは自分の職責の枠を決めず、当事者意識を持って発信できる人',
      body: 'FLARETECHに向いているのは、「主体的に動ける人」、そして「自分が関わっている仕事に対して、自分で勝手に枠をはめない人」です。\n\n「自分はプログラマーだから仕様をもらえないと書けません」と受け身になるのではなく、職責や領域を超えて「もっとこうすべきだ」と発信できる当事者意識を持った方が向いています。会議などで意見があるのに黙っていたり、「特にありません」と考えることを放棄してしまう姿勢は、当社では勿体ないと感じます。一言で言えば、「主体性」「当事者意識」、そして「今の自分に満足しない貪欲さ」を持った人ですね。\n\nFLARETECHは今、会社とチームをさらに大きくしていく「第二創業期」を迎えています。みんなで集まって組織を大きくしていくこのフェーズに共感し、当事者意識を持って飛び込んでいただける方は、ぜひ一緒に働きたいです！',
      image: img('shimizu', 'message.jpg')
    },
    skillGrowth: [],
    metaTitle: '清水 範人 | 社員インタビュー | FLARETECH 採用',
    metaDescription: '異業界からの再挑戦を経てITへ復帰した清水 範人さん（システム部 部長）のインタビュー。40名規模のPMと200名超のエンジニア組織を率いるリアル。'
  },
  {
    slug: 'misato-nakai',
    nameJa: '中井 海里',
    nameEn: 'Nakai Misato',
    department: 'システム部',
    job: 'engineer',
    jobLabel: '開発エンジニア',
    jobTagEn: 'Engineer',
    keywords: ['inexperienced', 'career-change'],
    previousJob: 'アパレルEC運営（異業種）',
    quote: 'ここなら安心して働ける環境だと感じたことが入社の決め手でした',
    catchCopy: '「PCスキルほぼゼロからエンジニアへ。コードに触れた楽しさが転職のきっかけ」',
    techStack: ['フロントエンド', 'MVVM'],
    relatedJobPath: '/jobs/development-engineer',
    thumbnail: img('misato-nakai', 'thumb.jpg'),
    heroImage: `${img('misato-nakai', 'hero.jpg')}?v=2`,
    profileImage: img('misato-nakai', 'profile.jpg'),
    heroObjectPosition: '36% 42%',
    hasArticle: true,
    featuredOnHome: true,
    status: 'published',
    order: 5,
    currentWork: {
      heading: '現在の業務',
      lead: '自販機アプリのフロントエンド開発',
      body: '現在はMVVMアーキテクチャを採用した自販機アプリの開発に携わり、商品搬出処理や画面表示ロジックの実装を担当しています。セッション情報をもとに、購入状況、診断経由・通常購入などの条件に応じたUIの分岐やメッセージ表示の切り替えを中心に実装しています。フロントエンドをメインに担当していますが、初めて扱う言語にも触れつつ修正にも取り組み、知識と技術の幅を広げています。'
    },
    careerBefore: {
      heading: 'FLARETECH入社前のキャリアと、転職のきっかけ',
      body: '前職では全く違う業界の仕事をしており、当時はPCスキルもほぼゼロでした（今もまだ怪しいところはありますが…！笑）。\n\nただ、PCを触ること自体に抵抗はなく、むしろ「もっと学びたい」という気持ちが強かったです。前職の業務で少しだけホームページを触る機会があり、そこからWebの世界に興味を持ちました。一念発起して大阪から東京へ上京し、まずはアパレル会社のECサイト運営に関わる仕事に就くことができました。\n\nEC運営では、自身でバリバリとコードを書くわけではなかったのですが、日々の業務の中でコードに触れる機会が多く、それがきっかけで本格的にエンジニアの道へ転職することを決意しました！'
    },
    joiningReason: {
      heading: 'FLARETECHを知ったきっかけと、入社の決め手',
      lead: '安心して働ける環境を求めて。「人とのつながり」が選んだ決め手',
      body: '一番の理由は、「人とのつながり（縦・横の関係性）を大切にできる会社」だと感じたからです。\n\nITエンジニアは、参画するプロジェクトによって業務内容や関わる人が大きく異なり、実際に現場が決まるまで環境が見えにくいという側面があります。そのため、プロジェクト先で困ったことや悩みが生じた際に、気軽に相談できる環境や、支えてくれる仲間の存在が何より重要だと考えていました。\n\n最終面接で役員の高木さんにお話を伺った際、メンバー同士の仲が良く、社内交流の機会も豊富だというエピソードをお聞きしました。自社メンバーや営業担当の方々との距離が近く、ここなら安心して働ける環境だと感じたことが入社の決め手でした。'
    },
    afterJoining: {
      heading: '入社後の変化',
      lead: '希望に寄り添う案件選択と、1on1で磨かれた課題解決力',
      body: '技術面とコミュニケーション面の両方で、確かな成長を実感しています。\n\n自分が「挑戦したい技術や分野」を伝えると、それに沿ったプロジェクトを提案していただけるため、扱える技術の幅が着実に広がりました。\n\nまた、営業担当の方との距離が非常に近く、定期的な1on1や現場からのフィードバックを通じて、自身の課題を客観的に把握できるようになりました。そこから、改善に向けて自ら考える力や、日々の課題解決力も身についたと感じています。'
    },
    flaretechStyle: {
      heading: 'FLARETECHらしさ',
      lead: '①現場で即活かせる、最先端のAI活用カルチャー',
      body: 'FLARETECHはAI活用に非常に前向きな会社で、最新の技術や活用事例に触れる機会が多く、日々の学びにつながっています。近年は「AI駆動開発」を取り入れる現場も増えているため、社内で得た知識やトレンドをプロジェクト先でもスムーズに活かせているのが面白いですね。',
      extraLead: '②役職や立場の壁を感じさせない、明るいコミュニケーション',
      extraBody: '社内の風通しが良く、出社しているメンバーも明るく話しやすい方が多いため、自然とコミュニケーションが生まれる雰囲気があります。役職や立場による壁を感じることも少なく、誰もが気軽に相談や意見交換ができる点もFLARETECHならではの魅力だと思います。'
    },
    message: {
      heading: 'FLARETECHが合う人とは / 応募者へのメッセージ',
      lead: '多様な個性を尊重する風土。お互いを高め合える仲間が集まる場所',
      body: '良い意味で「どんな人にも合う会社」だと思います。技術を突き詰めたい人、人とのつながりを大切にしたい人、新しいことに挑戦したい人など、本当にさまざまなバックグラウンドを持った方が在籍しています。お互いの個性を尊重する風土があるため、多様な価値観を受け入れ、刺激を受けながら働きたい方には特に合う環境です。\n\n一言で言うと、FLARETECHは「一人ひとりが成果やパフォーマンスを意識している会社」だと思います。会社の規模が大きくなると、「自分が少し手を抜いても分からないだろう」と考えてしまう人が出てきても不思議ではありません。しかし、私がこれまで関わってきた中には、そのような人が一人もいないんです。それぞれが自分の役割にしっかりと責任を持ち、より良い成果を出そうと前向きに取り組んでいる。そんな素敵なプロ意識を持った仲間が集まっている会社です！',
      image: img('misato-nakai', 'message.jpg')
    },
    skillGrowth: [],
    metaTitle: '中井 海里 | 社員インタビュー | FLARETECH 採用',
    metaDescription: 'PCスキルほぼゼロからエンジニアへ転身した中井 海里さんのインタビュー。人とのつながりを大切にできる環境を求めてFLARETECHへ。'
  },
  {
    slug: 'gen-kobayashi',
    nameJa: '小林 弦',
    nameEn: 'Kobayashi Gen',
    department: 'システム部',
    job: 'engineer',
    jobLabel: '開発エンジニア',
    jobTagEn: 'Engineer',
    keywords: ['career-change'],
    previousJob: '自動車の組み込み開発',
    quote: '成長する会社なので、多少失敗しても、成長できればいいよ',
    catchCopy: '「もっと裁量をもって仕事がしたい」厳格な組み込み開発からWeb領域へ',
    techStack: ['AWS', 'Laravel', 'バックエンド', '要件定義'],
    relatedJobPath: '/jobs/development-engineer',
    thumbnail: img('gen-kobayashi', 'thumb.jpg'),
    heroImage: img('gen-kobayashi', 'hero.jpg'),
    profileImage: img('gen-kobayashi', 'profile.jpg'),
    hasArticle: true,
    featuredOnHome: true,
    status: 'published',
    order: 6,
    currentWork: {
      heading: '現在の業務',
      lead: '電力管理システムのバックエンド開発。インフラから実装までを一気通貫で担当',
      body: '電力管理システム開発のプロジェクトに参画しています。独自開発の機器で収集した詳細な電力使用量をWebアプリケーション上でリアルタイムに可視化し、緻密な電力管理を可能にするシステムです。\n\n技術的にはAWSやLaravelを使用し、バックエンド開発を主軸に、インフラ構築から実装、テストまでを自身の裁量で一気通貫で行っています。'
    },
    careerBefore: {
      heading: 'FLARETECH入社前のキャリアと、転職のきっかけ',
      body: '前職では、自動車の組み込み開発を行っていました。近年の自動車は電動化が進み多くの半導体チップが搭載されているため、そこに書き込むソフトウェアの開発が主な任務でした。\n\n自動車業界は非常に規模が大きく、何より安全性が最優先されるため、挑戦できることや手順が厳格に決まっています。そのため、自分の工夫や裁量で大きな変化を生み出すことが難しい環境でした。\n\n以前から興味のあったWeb系で、自ら技術を磨き、お客さまと直接対話しながらシステムに自分の意思を反映していきたいと考え、思い切ってキャリアチェンジを決意しました。'
    },
    joiningReason: {
      heading: 'FLARETECHを知ったきっかけと、入社の決め手',
      lead: '会社の規模ではなく、経営陣の「オープンさ」と「勢い」に惹かれて',
      body: '求人サイトでたまたまFLARETECH（当時Marvel）の存在を目にしたのがきっかけです。カジュアル面談などで代表の河畑さんや役員の高木さんと実際にお話しした際、会社の現状やビジョンを非常にオープンに話してくださいました。何より「これからもっと成長していくぞ！」という強い勢いを感じ、お二人の人柄と熱意に惹かれて入社を決めました。'
    },
    afterJoining: {
      heading: '入社後の変化',
      lead: '技術の先にある「要件定義」。お客さまと仕様を創り上げる上流工程の楽しさ',
      body: '技術的な知識の幅が広がったことはもちろんですが、一番の成長は、お客さまと直接対話をして要望をシステムへと落とし込んでいく「要件定義」や「仕様策定」のフェーズができるようになってきたことです。'
    },
    flaretechStyle: {
      heading: 'FLARETECHらしさ',
      lead: 'Slackで役員とフラットに会話。ベンチャーらしい「手数の多さ」が面白い',
      body: '社内の風通しはかなり良いと思います。Slack上で社長の河畑さんや役員の高木さんと直接フラットに会話ができます。社内チャンネルを見ていても、メンバーがポンポン要望や意見を投稿し、それに対して役員陣が「ありがとうございます！了解です！」とフランクかつスピーディーに反応してくれる。この心理的安全性の高さはFLARETECHの良さだと思います。\n\nまた、社内勉強会やAIハッカソン、休日には社外の方も参加可能なオンラインセミナーなど、AIを貪欲に取り入れる企画が次々と推進されています。とにかく「手数が多い」ところにベンチャーならではの面白さを感じます。'
    },
    message: {
      heading: 'FLARETECHが合う人とは / 応募者へのメッセージ',
      lead: '「失敗しても、成長できればいい」あがきながら挑戦できる人と一緒に働きたい',
      body: 'FLARETECHには、多少未経験であっても、やる気があれば引き上げてくれる環境があります。最近はシステム部の組織化も進んでおり、私もリーダーを任せていただいていますが、現場の声を吸い上げる仕組みがどんどん整備されています。\n\nこれからエンジニアとして目指したいのは、技術だけで完結せず、よりビジネス視点を持った上流へのステップアップです。言われた通りに開発するのではなく、会社のビジネスに対して「技術の観点からアドバイスができる存在」を目指しています。\n\n一言で言うと、FLARETECHは「若い」会社です。だからこそ、失敗を恐れずに挑戦できる人に最適だと思います。実は私自身、本来は失敗を恐れるタイプなのですが、入社初期に高木さんが「Marvel（旧社名）は成長する会社なので、多少失敗しても、成長できればいいよ」と言ってくださったことがあります。\n\n未経験の方や新しい環境に飛び込む方は不安も大きいと思いますが、そんな挑戦を歓迎してくれる空気があります。一緒に頑張りましょう、挑戦しましょう、そして共にあがきながら成長していきましょう！',
      image: img('gen-kobayashi', 'message.jpg')
    },
    skillGrowth: [],
    metaTitle: '小林 弦 | 社員インタビュー | FLARETECH 採用',
    metaDescription: '自動車の組み込み開発からWeb領域へキャリアチェンジした小林 弦さんのインタビュー。裁量と上流工程に挑戦できるFLARETECHの働き方。'
  }
];

export const getLocalInterviews = () =>
  INTERVIEWS.filter((item) => item.status === 'published').sort((a, b) => a.order - b.order);

export const getLocalInterviewBySlug = (slug) =>
  INTERVIEWS.find((item) => item.slug === slug && item.status === 'published') || null;
